import { inject, type ProviderToken, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';
import { type Stitch } from '@modules/common/types/stitch';
import { of } from 'rxjs';
import { type DetailCrumb } from './detail-breadcrumbs.component';

/** The breadcrumbs' app crumb, one wording for every app (stitch-app-shell `appCrumb`). */
export const APP_CRUMBS = {
  mail: 'Mail app',
  calendar: 'Calendar app',
  tasks: 'Tasks app',
  notes: 'Notes app',
  contacts: 'Contacts app',
  files: 'Files app',
  sheets: 'Sheets app',
} as const;

/** A container page's middle crumb: the tree its container sits in, in the side nav. */
export const TREE_CRUMBS = {
  projects: 'Projects',
  notebooks: 'Notebooks',
  groups: 'Groups',
  folders: 'Folders',
  calendars: 'Calendars',
} as const;

/** The last crumb of an item or container with no title yet. */
export const UNTITLED_CRUMB = 'Untitled';

export interface DetailNavigationConfig<F extends string> {
  /** The app's crumb: "Mail app", "Tasks app"… */
  readonly appLabel: string;
  /** Panel 2's title for one of the app's views (the app's own title pipe); container pages name their tree instead. */
  readonly viewTitle?: (folder: F) => string;
}

export interface DetailCrumbsInput {
  /** The item's side in a team; nothing in a personal workspace. */
  readonly workspace?: 'team' | 'mine';
  /** A fixed middle crumb: a container page names its tree ("Projects", "Notebooks"…), whatever the list shows. */
  readonly view?: string;
  /** The list's view while a container is open in it, which then is the item's container. */
  readonly container?: string | null;
  /** The view named when neither a list view nor a container is known ("Tasks", "Inbox"…). */
  readonly fallbackView: string;
  readonly itemTitle: string;
  /**
   * The open item's id: the list view is read once per item, when it opens, so switching the side nav's view
   * afterwards does not move the open item there (Johanna, 2026-10-21: an Inbox email read "Spam" after a click on
   * Spam). Without it the crumb follows the list's current view.
   */
  readonly itemId?: string;
}

/**
 * stitch-app-shell detail-page navigation, one for every app (Johanna, 2026-10-20: every app follows the same
 * path): the breadcrumbs footer ([workspace ›] App › the list's view › the item) and the way a container badge
 * opens another item or the container. Only the main view (the layout's `ApplicationScope.MAIN`) has breadcrumbs;
 * the badge navigates there and in the side panel (panel 4), where it opens in the panel itself (Johanna,
 * 2026-10-21). Modals and shared pages keep their own framing. Create it in a form's field initializer (it injects).
 */
export class DetailNavigation<F extends string> {
  private readonly appState: BaseAppStateService<Stitch, Stitch, F> | null;

  /** Whether this page is the main view's, so it shows breadcrumbs and its badge navigates. */
  public readonly enabled: boolean;

  /** The side panel this page is open in, if it is: its badge opens items there. */
  private readonly sidePanel: DynamicPanelService | null;

  /** Whether the container badge opens its navigator: in the main view and in the side panel. */
  public readonly badgeNavigable: boolean;

  private readonly folder: Signal<F | null | undefined>;

  /** The list's view when the open item opened (see {@link DetailCrumbsInput.itemId}). */
  private opened: { itemId: string; folder: F | null | undefined } | null = null;

  /**
   * @param appStateToken the app's own state service (the form's context provides it)
   */
  public constructor(
    appStateToken: ProviderToken<BaseAppStateService<Stitch, Stitch, F>>,
    private readonly config: DetailNavigationConfig<F>,
  ) {
    this.appState = inject(appStateToken, { optional: true });
    const scope = inject(APPLICATION_SCOPE_TOKEN, { optional: true });
    this.enabled = !!this.appState && scope === ApplicationScope.MAIN;
    this.sidePanel =
      scope === ApplicationScope.DYNAMIC_PANEL ? inject(DynamicPanelService, { optional: true }) : null;
    this.badgeNavigable = this.enabled || !!this.sidePanel;
    this.folder = toSignal((this.enabled && this.appState?.getVirtualFolder()) || of(null), { initialValue: null });
  }

  public crumbs({ workspace, view, container, fallbackView, itemTitle, itemId }: DetailCrumbsInput): DetailCrumb[] {
    let folder = this.folder();
    if (itemId) {
      if (this.opened?.itemId !== itemId) {
        this.opened = { itemId, folder };
      }
      folder = this.opened.folder;
    }
    const viewTitle = this.config.viewTitle;
    return [
      ...(workspace ? [{ label: workspace === 'team' ? 'Team Workspace' : 'My Workspace' }] : []),
      { label: this.config.appLabel, action: (): void => this.appState?.setDefaultVirtualFolder() },
      // A view the app's title pipe does not name (a user folder, say) reads as the container instead.
      { label: view || (folder && viewTitle?.(folder)) || container || fallbackView },
      { label: itemTitle },
    ];
  }

  /**
   * Opens an item or a container in this view's detail panel, as a click in the list or the side nav does; in the
   * side panel, in the side panel (onto its Back stack), as a click in its list does.
   */
  public open(item: Stitch): void {
    if (this.sidePanel) {
      this.sidePanel.setFormItem(item);
      return;
    }
    this.appState?.setMainView(item);
  }
}
